import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Subject } from 'rxjs';
import { LocationTypeEnum } from '../shared/workbench-location/model/location-type.enum';
import { WorkbenchLocationService } from '../shared/workbench-location/service/workbench-location.service';
import { LocationSearchRequest } from '../shared/workbench-location/model/location-search-request.model';
import { Location } from '../shared/workbench-location/model/location';
import { FieldMapContext } from './field-map-context.model';

@Component({
    selector: 'jhi-workbench-location-list',
    templateUrl: './workbench-location-list.component.html',
    styleUrls: ['./workbench-location-list.component.css']
})
export class WorkbenchLocationListComponent implements OnInit, OnChanges, OnDestroy {

    @Input() locationTypes: LocationTypeEnum[] = [];
    @Input() parentLocationId?: number;
    @Output() onSelect = new EventEmitter<number>();
    @Output() onEditItem = new EventEmitter<LocationItem>();
    @Output() onDeleteItem = new EventEmitter<LocationItem>();
    @Input() searchPlaceholder = 'search';

    @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

    public readonly LocationTypeEnum = LocationTypeEnum;
    private readonly pageSize = 30;
    private currentPage = 0;
    private destroy$ = new Subject<void>();
    private locationsSubject = new BehaviorSubject<LocationItem[]>([]);

    locations$ = this.locationsSubject.asObservable();
    loading = false;
    allDataLoaded = false;
    currentLocationId: number;
    private searchSubject = new Subject<string>();
    searchTerm = '';

    constructor(private workbenchLocationService: WorkbenchLocationService, private fieldMapContext: FieldMapContext) {
    }

    ngOnInit() {
        // Initial load
        this.loadMore();

        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((searchTerm) => {
            this.resetAndSearch();
        }
        );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onEdit(item: LocationItem, event: Event): void {

        if (!this.fieldMapContext.mouseEventListenerEnabled) {
            return;
        }

        // Prevent item selection when clicking edit
        event.stopPropagation();
        event.preventDefault();
        this.onEditItem.emit(item);
    }

    onDelete(item: LocationItem, event: Event): void {
        if (!this.fieldMapContext.mouseEventListenerEnabled) {
            return;
        }
        // Prevent item selection when clicking delete
        event.stopPropagation();
        event.preventDefault();
        this.onDeleteItem.emit(item);
    }

    onItemSelect(locationItem: LocationItem, $event): void {
        $event.stopPropagation();
        $event.preventDefault();
        if (!this.fieldMapContext.mouseEventListenerEnabled) {
            return;
        }

        this.currentLocationId = locationItem.location.id;
        if (this.onSelect) {
            this.onSelect.emit(this.currentLocationId);
        }
    }

    refresh() {
        this.currentPage = 0;
        this.allDataLoaded = false;
        this.locationsSubject.next([]);
        this.loadMore();
    }

    onScroll(index: number) {
        // Calculate if we need to load more data
        const end = this.viewport.getRenderedRange().end;
        const total = this.viewport.getDataLength();

        // Load more if we're near the end and not already loading
        if (end > total - 10 && !this.loading && !this.allDataLoaded) {
            this.loadMore();
        }
    }

    private loadMore() {
        if (this.loading || this.allDataLoaded) {
            return;
        }

        this.loading = true;

        const request = new LocationSearchRequest();
        request.locationTypeIds = this.locationTypes;
        if (this.parentLocationId) {
            request.parentLocIds = [this.parentLocationId];
        }
        if (this.searchTerm) {
            request.locationNameFilter = {
                type: 'STARTSWITH',
                value: this.searchTerm
            };
        }

        const pagination = {
            page: this.currentPage,
            size: this.pageSize
        };

        this.workbenchLocationService.searchLocations(request, pagination).pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (response) => {
                const newLocations = response.body.map((o: Location) => new LocationItem(o)) || [];
                const currentLocations = this.locationsSubject.value;

                // Check if we've received less items than requested (meaning we're at the end)
                if (newLocations.length < this.pageSize) {
                    this.allDataLoaded = true;
                }

                // Append new locations to existing ones
                this.locationsSubject.next([...currentLocations, ...newLocations]);
                this.currentPage++;
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading locations:', error);
                this.loading = false;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.refresh();
    }

    isItemSelected(item: LocationItem) {
        if (this.locationTypes.includes(LocationTypeEnum.FIELD)) {
            if (this.fieldMapContext.fieldsMap.has(item.location.id.toString())) {
                return this.fieldMapContext.fieldsMap.get(item.location.id.toString()).isSelected;
            }
        } else if (this.locationTypes.includes(LocationTypeEnum.BLOCK)) {
            if (this.fieldMapContext.blocksMap.has(item.location.id.toString())) {
                return this.fieldMapContext.blocksMap.get(item.location.id.toString()).isSelected;
            }
        }
    }

    isItemEditing(item: LocationItem) {
        if (this.locationTypes.includes(LocationTypeEnum.FIELD)) {
            if (this.fieldMapContext.fieldsMap.has(item.location.id.toString())) {
                return this.fieldMapContext.fieldsMap.get(item.location.id.toString()).isEditing;
            }
        } else if (this.locationTypes.includes(LocationTypeEnum.BLOCK)) {
            if (this.fieldMapContext.blocksMap.has(item.location.id.toString())) {
                return this.fieldMapContext.blocksMap.get(item.location.id.toString()).isEditing;
            }
        }
        return false;
    }

    model(item) {
        if (this.locationTypes.includes(LocationTypeEnum.FIELD)) {
            if (this.fieldMapContext.fieldsMap.has(item.location.id.toString())) {
                return this.fieldMapContext.fieldsMap.get(item.location.id.toString());
            }
        } else if (this.locationTypes.includes(LocationTypeEnum.BLOCK)) {
            if (this.fieldMapContext.blocksMap.has(item.location.id.toString())) {
                return this.fieldMapContext.blocksMap.get(item.location.id.toString());
            }
        }
    }

    onSearchChange(term: string) {
        this.searchSubject.next(term);
    }

    clearSearch() {
        this.searchTerm = '';
        this.onSearchChange('');
    }

    private resetAndSearch() {
        this.currentPage = 0;
        this.allDataLoaded = false;
        this.locationsSubject.next([]);
        this.loadMore();
    }

    isButtonDisabled() {
        // Disable the button if the global mouse event listener is disabled.
        return !this.fieldMapContext.mouseEventListenerEnabled;
    }
}

export class LocationItem {
    constructor(
        public location: Location
    ) {
    }
}
